import styled from "styled-components";
import { useState, useContext, useEffect, useRef } from "react";
import { CurrentUserContext } from "../../Contexts/CurrentUserContext";
import { StoriesContext } from "../../Contexts/StoriesContext";
import { InputTextarea } from "primereact/inputtextarea";
import { ConfirmDialog } from "primereact/confirmdialog";
import { confirmDialog } from "primereact/confirmdialog";

import { Button } from "primereact/button";
import { generateRandomString } from "../../Helpers/ReportDataFunctions";
import {
  writeAndEditStory,
  listenToTenMostRecentStories,
  deleteStory,
} from "../../Firebase";
import { Timestamp } from "firebase/firestore";
import { StoryData } from "../../Types/Database/StoriesData";

const StoriesIndexContainer = styled.div`
  width: 100%;
  padding: 15px 0;
  h1 {
    margin: 10px;
    color: var(--accent-color-dark);
    font-size: 25px;
    font-weight: 500;
  }
  @media (max-width: 400px) {
    h1 {
      font-size: 20px;
    }
  }
`;

const StoriesWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  justify-content: center;
  width: 100%;
  padding-bottom: 60px;
`;

const StoryBubble = styled.div`
  padding: 10px;
  margin: 10px;
  border-radius: 5px;
  box-shadow: 0px 0px 10px 0px rgba(185, 185, 185, 0.75);

  @media (max-width: 940px) {
    max-width: 100%;
  }

  span {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  i {
    font-size: 12px;
    margin-left: 5px;
  }
  i.pi-pencil:hover {
    color: green;
    cursor: pointer;
  }
  i.pi-times:hover {
    color: #cd1309;
    cursor: pointer;
  }
  h2 {
    font-size: 17px;
    color: var(--accent-color-dark);
    margin-bottom: 3px;
  }
  h3 {
    font-size: 12px;
    margin-top: 5px;
  }
  p {
    font-size: 13px;
  }
`;

const StoryEntry = styled.div`
  position: fixed;
  z-index: 300;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 7px 15px 7px 10px;
  gap: 10px;
  background-color: var(--accent-color-dark);
  transition: 300ms;
  height: 60px;
  width: calc(100% - 200px);
  left: 200px;
  bottom: 0;

  &:focus-within {
    height: 100px;
  }

  textarea:focus {
    height: 80px;
  }

  @media (max-width: 800px) {
    width: 100%;
    left: 0;
    bottom: 80px;

    &:focus-within {
      height: 130px;
      transform: translateY(86px);
    }

    textarea:focus {
      height: 100px;
    }
    &:focus-within .charCount {
      bottom: 14px;
    }
  }

  textarea {
    position: relative;
    resize: none;
    width: 100%;
    font-size: 14px;
    padding: 5px;
    border-radius: 9px;
    height: 46px;
    transition: 300ms;
  }

  .charCount {
    position: absolute;
    bottom: 5px;
    right: 58px;
    font-size: 12px;
    color: #797979;
    padding: 5px;
    user-select: none;
  }

  i {
    color: white;
    border-radius: 50%;
    width: 30px;
    height: 30px;
    display: flex;
    justify-content: center;
    align-items: center;
    transition: 200ms;
  }
  i:hover {
    box-shadow: 0px 0px 10px 5px rgba(0, 0, 0, 0.257);
    cursor: pointer;
  }
  i:active {
    box-shadow: none;
    cursor: default;
  }
`;

const SeeMore = styled.div`
  width: 100%;
  grid-column: 1 / -1;
  display: flex;
  justify-content: center;
`;

export default function StoriesIndex(props: Props) {
  const [newStory, setNewStory] = useState<string>("");
  const [charCount, setCharCount] = useState<number>(0);
  const [stories, setStories] = useState<StoryData[]>([]);
  const storyContext = useContext(StoriesContext);
  const userContext = useContext(CurrentUserContext);
  const user = userContext?.currentUser;
  const storiesWrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setStories(storyContext.stories);
  }, [storyContext.stories]);

  function handleDelete(storyId: string) {
    confirmDialog({
      message: "Are you sure you want to delete this story?",
      header: "Confirmation",
      icon: "pi pi-exclamation-triangle",
      accept: () => {
        setStories(stories.filter((story) => story.storyId !== storyId));
        deleteStory(storyId);
      },
      reject: () => {
        // User rejected the deletion, you can handle any logic here if needed
      },
    });
  }

  function handleSend() {
    if (newStory.length > 0 && user && user.firebaseUid) {
      const countryOfUser = "A1B2C3D4E1";
      const storyToSave = {
        firstName: user.firstName,
        lastName: user.lastName,
        story: newStory,
        firebaseUid: user.firebaseUid[0],
        storyId: generateRandomString(),
        date: Timestamp.now(),
        teamLevel: countryOfUser,
      };
      setStories([storyToSave, ...stories]);
      writeAndEditStory(storyToSave);
      setNewStory("");
      setCharCount(0);
      if (storiesWrapperRef.current) {
        storiesWrapperRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
      }
    }
  }

  function formatTimestampToDateStr(timestamp: Timestamp) {
    const date = timestamp.toDate();
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);

    return `${day}/${month}/${year}`;
  }

  function isAllowdToEditStory(story: StoryData) {
    if (user && user.PCRoles.some((role: UserRole) => role.role === "admin")) {
      return true;
    }
    if (user && user.firebaseUid?.includes(story.firebaseUid)) {
      return true;
    } else {
      return false;
    }
  }

  return (
    <StoriesIndexContainer ref={storiesWrapperRef}>
      <h1>Recent Good Reports</h1>

      <StoriesWrapper>
        {stories.map((story, index) => {
          return (
            <StoryBubble key={`story-bubble-${index}`}>
              <span key={`bubble-span-${index}`}>
                <h2 key={`story-name-${index}`}>
                  {story.firstName} {story.lastName}
                </h2>
                <div key={`story-edit-box-${index}`}>
                  {isAllowdToEditStory(story) && (
                    <i
                      className="pi pi-times"
                      key={`story-delete-button-${index}`}
                      onMouseDown={() => handleDelete(story.storyId)}
                    ></i>
                  )}
                </div>
              </span>
              <p key={`story-text-${index}`}>{story.story}</p>
              <h3 key={`story-date-${index}`}>
                {formatTimestampToDateStr(story.date)}
              </h3>
            </StoryBubble>
          );
        })}
        <SeeMore>
          <Button
            label="See More"
            icon="pi pi-chevron-down"
            iconPos="right"
            className="p-button-text"
            onClick={() => storyContext.getTenMoreStories()}
          />
        </SeeMore>
      </StoriesWrapper>

      <ConfirmDialog />
      <StoryEntry>
        <InputTextarea
          value={newStory}
          onChange={(e) => {
            setNewStory(e.target.value);
            setCharCount(e.target.value.length);
          }}
          rows={2}
          maxLength={200}
          placeholder="Please share your story in under 200 characters and anonymize sensitive information."
        />
        <div className="charCount">{charCount}/200</div>
        <i className="pi pi-send" onMouseDown={handleSend}></i>
      </StoryEntry>
    </StoriesIndexContainer>
  );
}

interface Props {}
